// src/components/legal/IngredientCheckerPrivacyPolicy.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #9BB167;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #9BB167;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '\2190';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const CompanyInfo = styled.div`
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    white-space: pre-wrap;
`;

const InfoBox = styled.div`
    background: #F0F4E8;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const IngredientCheckerPrivacyPolicy = () => {
    return (
        <Container>
            <BackLink to="/ingredientchecker">성분체크로 돌아가기</BackLink>

            <Header>
                <Title>성분체크 개인정보처리방침</Title>
                <UpdateDate>최종 수정일: 2026년 3월 16일</UpdateDate>
            </Header>

            <CompanyInfo>
                <SectionTitle>데이터 컨트롤러 정보</SectionTitle>
                <Paragraph>
                    • 회사명: 마인드월드(MindWorld)
                    • 대표자: 윤우재
                    • 주소: 경기도 양주시 덕계로 137
                    • 사업자등록번호: 504-43-28680
                    • 이메일: mind-wellness@naver.com
                    • 서비스: 성분체크(Ingredient Checker) 모바일 앱
                </Paragraph>
            </CompanyInfo>

            <Section>
                <SectionTitle>1. 개인정보 처리 개요</SectionTitle>
                <Paragraph>
                    마인드월드(MindWorld, 이하 "회사")는 사용자의 개인정보 보호를 매우 중요하게 생각합니다. 본 개인정보처리방침은 EU 일반데이터보호규정(GDPR), 캘리포니아 소비자 개인정보보호법(CCPA), 한국 개인정보보호법 등 관련 법규를 준수하여 작성되었습니다.

                    본 방침은 성분체크 앱 및 관련 서비스 이용 시 수집, 처리, 저장되는 개인정보에 적용됩니다.

                    성분체크는 AI 식품/화장품 성분 스캐너로, 바코드 스캔으로 식품 성분 위험도를 분석하고 건강 점수를 제공하는 앱입니다. 본 서비스는 식품 성분 분석 서비스이며, 의료 데이터를 수집하거나 처리하지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. 개인정보 처리의 법적 근거</SectionTitle>
                <Paragraph>
                    회사는 다음의 법적 근거에 따라 개인정보를 처리합니다:

                    • 계약 이행: 서비스 제공을 위해 필요한 처리
                    • 동의: 사용자의 명시적 동의를 받은 처리
                    • 정당한 이익: 서비스 개선, 보안, 사기 방지 등
                    • 법적 의무 준수: 관련 법령에 따른 의무 이행
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. 수집하는 개인정보의 항목</SectionTitle>
                <Paragraph>
                    필수 정보:
                    • 이메일 주소 (계정 생성 및 인증)
                    • 기기 정보 (OS 종류, 버전, 기기 식별자)
                    • 서비스 이용 로그

                    스캔 및 분석 관련 정보:
                    • 바코드 스캔 기록 (바코드 번호, 스캔 시간)
                    • 즐겨찾기 제품 목록
                    • 알레르기 및 식품 선호도 설정
                    • 유저 업로드 제품 이미지

                    자동 수집 정보:
                    • IP 주소 (지역 정보 확인용, 개인 식별 불가 형태로 처리)
                    • 앱 사용 패턴 및 성능 데이터
                    • 쿠키 및 유사 기술을 통한 정보
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. 개인정보 처리 목적</SectionTitle>
                <Paragraph>
                    서비스 제공:
                    • 계정 관리
                    • 바코드 스캔 및 제품 성분 분석
                    • 건강 점수(100점 만점) 산출
                    • 첨가물 위험도 분석
                    • 영양성분 시각화
                    • AI 성분 분석
                    • 제품 검색
                    • 식품 선호도 알림
                    • 고객 지원 및 문의 응답

                    서비스 개선:
                    • 새로운 분석 기능 개발
                    • 사용자 경험 분석 및 개선
                    • 서비스 안정성 및 보안 향상

                    법적 의무:
                    • 관련 법령에 따른 의무 이행
                    • 분쟁 해결 및 법적 절차 대응
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. 개인정보 보유 및 삭제</SectionTitle>
                <Paragraph>
                    일반 개인정보:
                    • 계정 활성 상태 동안 보유
                    • 계정 삭제 시 30일 이내 완전 삭제

                    스캔 및 분석 데이터:
                    • 사용자가 직접 삭제하거나 계정 삭제 시 즉시 완전 삭제
                    • 백업 시스템에서도 30일 이내 완전 제거

                    익명화된 통계 데이터:
                    • 개인 식별이 불가능하도록 처리된 데이터는 서비스 개선 목적으로 보관 가능

                    법적 보관 의무:
                    • 관련 법령에 따라 보관이 요구되는 경우 해당 기간까지 보관
                    • 결제 기록: 관련 법령에 따라 보관

                    삭제 방법:
                    • 전자 파일: 복구 불가능한 방식으로 완전 삭제
                    • 데이터베이스: 즉시 삭제 후 백업에서도 완전 제거
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. 개인정보 국제 전송</SectionTitle>
                <Paragraph>
                    서비스 제공을 위해 개인정보가 다음 지역으로 전송될 수 있습니다:

                    • 미국: Firebase (Google), Supabase 서버 위치

                    국제 전송 보호 조치:
                    • EU-US 데이터 프라이버시 프레임워크 준수
                    • 표준 계약 조항 (Standard Contractual Clauses) 적용
                    • 적절성 결정 국가로의 전송 우선
                    • 암호화 및 기타 기술적 보호조치 적용

                    EU 사용자의 경우 데이터 전송에 대한 이의를 제기할 권리가 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. 제3자 제공 및 공유</SectionTitle>
                <Paragraph>
                    회사는 사용자의 명시적 동의 없이 개인정보를 제3자에게 판매, 임대, 공유하지 않습니다.

                    다음의 경우에만 제한적으로 공유됩니다:

                    서비스 제공업체:
                    • Google (Firebase): 인증, 분석, 알림
                    • Supabase: 데이터베이스 호스팅
                    • Google: Google Play Store 결제 처리
                    • Open Food Facts: 제품 데이터베이스 조회

                    법적 요구사항:
                    • 법원 명령, 소환장 등 법적 절차
                    • 사용자 또는 제3자 안전 보호를 위한 긴급 상황
                    • 사기 방지 및 보안 위협 대응

                    모든 서비스 제공업체는 엄격한 데이터 보호 계약에 따라 운영됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. 사용자 권리 및 선택</SectionTitle>
                <Paragraph>
                    모든 사용자 권리:
                    • 개인정보 열람권
                    • 정정 및 삭제권
                    • 처리 제한권
                    • 데이터 이동권 (내보내기)
                    • 이의 제기권

                    EU 사용자 추가 권리 (GDPR):
                    • 자동화된 의사결정 거부권
                    • 감독기관 신고권
                    • 효과적인 사법적 구제를 받을 권리

                    캘리포니아 사용자 권리 (CCPA):
                    • 개인정보 판매 거부권 (당사는 판매하지 않음)
                    • 차별받지 않을 권리

                    권리 행사 방법:
                    • 앱 내 설정 메뉴
                    • 이메일: mind-wellness@naver.com
                    • 요청 후 30일 이내 응답
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. 쿠키 및 추적 기술</SectionTitle>
                <Paragraph>
                    회사는 다음 기술을 사용합니다:

                    필수 쿠키:
                    • 로그인 상태 유지
                    • 보안 및 사기 방지
                    • 기본 앱 기능 제공

                    분석 쿠키 (선택적):
                    • 앱 사용 통계 분석
                    • 성능 모니터링
                    • 사용자 경험 개선

                    사용자 선택권:
                    • 필수 쿠키: 서비스 이용을 위해 필요
                    • 분석 쿠키: 앱 설정에서 거부 가능
                    • 브라우저 설정에서 쿠키 관리 가능

                    EU 사용자는 쿠키 배너를 통해 동의를 관리할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. 데이터 보안</SectionTitle>
                <Paragraph>
                    회사는 다음과 같은 보안 조치를 적용합니다:

                    기술적 보호조치:
                    • 종단간 암호화 (E2E)
                    • TLS/SSL 통신 암호화
                    • 정기적 보안 감사
                    • 침투 테스트
                    • 접근 권한 최소화 원칙

                    관리적 보호조치:
                    • 직원 보안 교육
                    • 정기적 보안 정책 검토
                    • 사고 대응 절차
                    • 제3자 보안 인증 요구

                    물리적 보호조치:
                    • 보안이 확보된 데이터센터 이용
                    • 24/7 모니터링
                    • 이중 인증 시스템

                    보안 사고 시 72시간 이내 관련 기관 및 사용자에게 통지합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. 연락처 및 데이터 보호 담당자</SectionTitle>
                <Paragraph>
                    개인정보 보호 관련 문의:

                    데이터 보호 담당자: 윤우재
                    이메일: mind-wellness@naver.com
                    회사: 마인드월드(MindWorld)
                    주소: 경기도 양주시 덕계로 137

                    EU 사용자 추가 연락처: 추후 서비스시 대리인 선임 예정.
                    • EU 대표자: [향후 지정 예정]
                    • 감독기관: 해당 국가 데이터 보호 기관

                    문의 응답 시간:
                    • 일반 문의: 7일 이내
                    • 권리 행사 요청: 30일 이내
                    • 긴급 보안 사안: 24시간 이내
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. 개인정보처리방침 변경</SectionTitle>
                <Paragraph>
                    본 방침의 변경 시 다음과 같이 공지합니다:

                    일반 변경사항:
                    • 시행 7일 전 앱 내 공지
                    • 이메일 또는 푸시 알림

                    중요 변경사항 (수집 항목, 목적, 제3자 제공 등):
                    • 시행 30일 전 사전 공지
                    • 명시적 동의 재요청 (필요시)

                    사용자 선택권:
                    • 변경사항에 동의하지 않을 경우 서비스 이용 중단 및 계정 삭제 가능
                    • 중요 변경사항의 경우 별도 동의 절차

                    과거 버전의 방침은 요청 시 제공 가능합니다.
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>방침 정보</SectionTitle>
                <Paragraph>
                    • 최초 작성일: 2026년 3월 16일
                    • 최종 수정일: 2026년 3월 16일
                    • 시행일: 2026년 3월 16일
                    • 버전: 1.0
                    • 적용 법규: GDPR, CCPA, 한국 개인정보보호법
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default IngredientCheckerPrivacyPolicy;
